import { useInfiniteQuery, useMutation, useQuery } from 'react-query'
import { message } from 'antd'
import {
  addUser,
  deleteUser,
  getUser,
  getUsers,
  resendInvitation,
  updateReadNews,
  updateUser,
} from 'utils/apis/users'
import {
  acceptInvitation,
  changePassword,
  generateOtp,
  getUserByToken,
  globalSearch,
  invite,
  loginWithSso,
  resetPin,
  revokeLogin,
  singleLogin,
  updateProfile,
  validatePin,
  verifyFacebookToken,
  verifyGoogleToken,
} from 'utils/apis/user'
import { useDispatch } from 'react-redux'
import { updateUser as updateUserRedux } from 'app-redux/initSlice'

export function useUsers({ payload, options = {}, config } = {}) {
  return useQuery(
    ['users', payload],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getUsers(payload, config)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useUser = ({ payload, options = {} }) => {
  return useQuery(
    ['user', payload],
    async () => {
      const {
        data: { data },
      } = await getUser(payload)
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useLoginWithSsoMutation = () => {
  return useMutation(loginWithSso)
}

export const useRegister = () => {
  return useMutation(invite)
}

export const useAcceptInvitation = () => {
  return useMutation(acceptInvitation)
}

export function useUserByToken(payload, options = {}) {
  return useQuery(
    ['userByToken', payload.token],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getUserByToken(payload.token)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useSingleLogin = () => {
  return useMutation((payload) =>
    singleLogin(payload.email, payload.password, payload.remember_me, payload.is_otp),
  )
}

export const useVerifyGoogleToken = () => {
  return useMutation((accessToken) => verifyGoogleToken(accessToken))
}

export const useVerifyFacebookToken = () => {
  return useMutation((accessToken) => verifyFacebookToken(accessToken))
}

export const useRevokeLogin = () => {
  return useMutation(() => revokeLogin())
}

export const useUpdateProfile = () => {
  return useMutation((payload) => updateProfile(payload))
}

export const useChangePassword = () => {
  return useMutation((payload) => changePassword(payload))
}

export const useGenerateOtp = () => {
  return useMutation(generateOtp)
}

export const useUpdateReadNews = () => {
  const dispatch = useDispatch()
  return useMutation(updateReadNews, {
    onMutate: () => {
      dispatch(updateUserRedux({ is_show_news: 0 }))
    },
    onSuccess: (response) => {
      if (!response.data.success) {
        dispatch(updateUserRedux({ is_show_news: 1 }))
      }
    },
    onError: () => {
      dispatch(updateUserRedux({ is_show_news: 1 }))
    },
  })
}

export const useAddUserMutation = () => {
  return useMutation(addUser)
}

export const useUpdateUserMutation = () => {
  return useMutation(({ data, id }) => updateUser(data, id))
}

export const useDeleteUserMutation = () => {
  return useMutation(deleteUser)
}

export const useResendInvitationMutation = () => {
  return useMutation(resendInvitation)
}

export const useResetPin = () => {
  return useMutation(({ apiOptions, ...data }) => resetPin(data, apiOptions))
}

export const useValidatePin = () => {
  return useMutation(({ apiOptions, ...data }) => validatePin(data, apiOptions))
}

export const useInfiniteGlobalSearch = (payload = {}, options = {}) => {
  return useInfiniteQuery(
    ['infiniteGlobalSearch', payload],
    async ({ pageParam }) => {
      let response
      try {
        const _params = payload
        if (pageParam) {
          _params.cursor = pageParam
        }
        const {
          data: { data },
        } = await globalSearch(_params)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.hasMore ? lastPage.nextCursor : undefined
      },
      onError: (error) => message.error(error.message || 'Failed to load data from server!'),
      keepPreviousData: true,
      ...options,
    },
  )
}
